
import { Helmet } from 'react-helmet-async';
import { getImageUrl, getPageUrl } from '@/utils/seo/urlUtils';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: 'website' | 'article' | 'profile';
  twitterCard?: 'summary' | 'summary_large_image';
  canonicalUrl?: string;
  noIndex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  authorName?: string;
  locale?: string;
  alternateLocales?: Array<{locale: string, url: string}>;
  articleSection?: string;
  articleTags?: string[];
  structuredData?: object;
}

const defaultDescription = "ReCut - Plateforme de création et de monétisation de contenu pour les créateurs vidéo";
const defaultKeywords = "recut, création vidéo, monetisation, créateurs, contenu, vidéo, montage";
const defaultOgImage = "/lovable-uploads/0c5638fb-e14b-45c9-962d-70e9659a760f.png";

export const SEO = ({
  title,
  description = defaultDescription,
  keywords = defaultKeywords,
  ogImage = defaultOgImage,
  ogUrl,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  canonicalUrl,
  noIndex = false,
  publishedTime,
  modifiedTime,
  authorName,
  locale = 'fr_FR',
  alternateLocales,
  articleSection,
  articleTags,
  structuredData
}: SEOProps) => {
  const siteTitle = title ? `${title} | ReCut App` : 'ReCut App';
  const fullOgImage = getImageUrl(ogImage);
  const pageUrl = ogUrl ? getPageUrl(ogUrl) : undefined;
  const fullCanonicalUrl = canonicalUrl ? getPageUrl(canonicalUrl) : pageUrl;
  
  return (
    <Helmet>
      <title>{siteTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Robots directives */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      {ogImage && <meta property="og:image" content={fullOgImage} />}
      {pageUrl && <meta property="og:url" content={pageUrl} />}
      <meta property="og:locale" content={locale} />
      
      {/* Article specific og tags */}
      {ogType === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {ogType === 'article' && authorName && (
        <meta property="article:author" content={authorName} />
      )}
      {ogType === 'article' && articleSection && (
        <meta property="article:section" content={articleSection} />
      )}
      {ogType === 'article' && articleTags && articleTags.map((tag, index) => (
        <meta key={`tag-${index}`} property="article:tag" content={tag} />
      ))}
      
      {/* Alternate language versions */}
      {alternateLocales && alternateLocales.map(({locale, url}) => (
        <link 
          key={locale} 
          rel="alternate" 
          hrefLang={locale} 
          href={getPageUrl(url)} 
        />
      ))}
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={fullOgImage} />}
      
      {/* Canonical URL */}
      {fullCanonicalUrl && <link rel="canonical" href={fullCanonicalUrl} />}
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};
